// shared/overlay – notes as their own layer over a PDF (contract LY, rimodocs/docs/API.md „Vertrag LY – Ebenen auf PDFs“;
// owner RimoCompta, 02.11.2026). The ORIGINAL IS NEVER CHANGED: the program keeps the layer next to the document (RimoCompta:
// at the incoming invoice) and lays it over the page when showing (LayerView), printing or exporting (server/burn.ts).
// Coordinates are PDF points (1/72"), origin top left, on the page AS DISPLAYED (/Rotate applied).
// layoutItem() turns an item into finished drawing operations measured with the font width tables (metrics.ts): the browser
// (SVG) and the server (pdf-lib) draw exactly these, so preview and print break and size text identically.
// Framework-free and ESM/CJS-neutral (no import.meta, no top-level await): RimoCompta's API copies it (CommonJS).
import { METRICS, type FontKey } from './metrics.js';

export interface LayerRow { label: string; value: string; strong?: boolean; mono?: boolean }
export interface LayerLine { text: string; bold?: boolean; mono?: boolean; size?: number; align?: 'left' | 'center' | 'right'; color?: string }
export interface LayerRect { page: number; x: number; y: number; w: number; h: number }
export interface LayerItem {
  id: string;
  page: number;
  x: number; y: number; w: number; h: number;
  /** box (escompte), stamp (payée), text. Other kinds are kept but not drawn (RimoDocs' later kinds). */
  kind: string;
  accent: string;
  border?: 'dashed' | 'solid' | 'none';
  /** A small program mark (key into the marks the caller passes – PNG from brand/), top right. */
  mark?: string;
  /** A picture inside the item (key like `mark`), e.g. a stamp with a logo – kept in the model, drawn left of the lines. */
  image?: string;
  title?: string;
  rows?: LayerRow[];
  lines?: LayerLine[];
  /** Stamp tilt −15 … 15 degrees (around the item's centre). */
  tilt?: number;
  /** Quarter turns 0/90/180/270 (contract LY, later use). */
  rotate?: number;
  opacity?: number;
  /** The suggestion (RimoAI / fallback): „Zurücksetzen“ goes back to it; sure = false → „Position prüfen“. */
  suggested?: LayerRect & { sure: boolean };
  limits?: { minW: number; maxW: number; minH: number; maxH: number };
}
export interface Layer { v: 1; items: LayerItem[] }
export interface PageSize { w: number; h: number }

const HEX = /^#[0-9a-fA-F]{6}$/;
const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const KINDS_DRAWN = new Set(['box', 'stamp', 'text']);
export const isDrawn = (it: LayerItem) => KINDS_DRAWN.has(it.kind);

/**
 * A stored layer as data from the database or a request → a checked Layer. Unknown kinds stay (not drawn); coordinates are
 * clamped onto the page (`pages[i]` = size of page i+1 as displayed), sizes into the item's limits. Throws on wrong shapes.
 */
export function validateLayer(raw: unknown, pages?: PageSize[]): Layer {
  const r = (raw ?? {}) as { v?: unknown; items?: unknown };
  if (r.v !== undefined && r.v !== 1) throw new Error('layer: unknown version');
  if (!Array.isArray(r.items)) throw new Error('layer: items missing');
  const items = r.items.map((el, i): LayerItem => {
    const it = (el ?? {}) as Record<string, unknown>;
    if (typeof it.id !== 'string' || !it.id) throw new Error(`layer: item ${i} without id`);
    if (typeof it.kind !== 'string' || !it.kind) throw new Error(`layer: item ${it.id} without kind`);
    const accent = typeof it.accent === 'string' && HEX.test(it.accent) ? it.accent : '#1375BD';
    const limits = it.limits as LayerItem['limits'] | undefined;
    let w = Math.max(8, num(it.w, 120)), h = Math.max(8, num(it.h, 60));
    if (limits) { w = clamp(w, limits.minW, limits.maxW); h = clamp(h, limits.minH, limits.maxH); }
    const page = Math.max(1, Math.round(num(it.page, 1))), size = pages?.[page - 1];
    let x = num(it.x, 0), y = num(it.y, 0);
    if (size) { w = Math.min(w, size.w); h = Math.min(h, size.h); x = clamp(x, 0, size.w - w); y = clamp(y, 0, size.h - h); }
    return { ...(it as unknown as LayerItem), id: it.id, kind: it.kind, page, x, y, w, h, accent,
      tilt: it.tilt === undefined ? undefined : clamp(num(it.tilt, 0), -15, 15),
      rotate: it.rotate === undefined ? undefined : ((Math.round(num(it.rotate, 0) / 90) * 90) % 360 + 360) % 360,
      opacity: it.opacity === undefined ? undefined : clamp(num(it.opacity, 1), 0.3, 1) };
  });
  return { v: 1, items };
}

/** „Zurücksetzen“: the item back on its suggestion (unchanged when it has none). */
export function resetItem(it: LayerItem): LayerItem {
  return it.suggested ? { ...it, page: it.suggested.page, x: it.suggested.x, y: it.suggested.y, w: it.suggested.w, h: it.suggested.h } : it;
}

/**
 * A rectangle in the page's unrotated PDF space (pdftotext -bbox: origin top left of the MediaBox) → the displayed page
 * (/Rotate 0/90/180/270 applied), and back. `media` = unrotated page size in points.
 */
export function toDisplayed(r: { x: number; y: number; w: number; h: number }, rotate: number, media: PageSize) {
  switch (((rotate % 360) + 360) % 360) {
    case 90: return { x: media.h - r.y - r.h, y: r.x, w: r.h, h: r.w };
    case 180: return { x: media.w - r.x - r.w, y: media.h - r.y - r.h, w: r.w, h: r.h };
    case 270: return { x: r.y, y: media.w - r.x - r.w, w: r.h, h: r.w };
    default: return { ...r };
  }
}
export function fromDisplayed(r: { x: number; y: number; w: number; h: number }, rotate: number, media: PageSize) {
  switch (((rotate % 360) + 360) % 360) {
    case 90: return { x: r.y, y: media.h - r.x - r.w, w: r.h, h: r.w };
    case 180: return { x: media.w - r.x - r.w, y: media.h - r.y - r.h, w: r.w, h: r.h };
    case 270: return { x: media.w - r.y - r.h, y: r.x, w: r.h, h: r.w };
    default: return { ...r };
  }
}

/** Width of a text in points with a font of the width tables. */
export function textWidth(text: string, font: FontKey, size: number): number {
  const m = METRICS[font];
  let u = 0; for (const ch of text) u += m.widths[ch] ?? m.fallback;
  return (u * size) / 1000;
}
/** Words into lines no wider than `max` (a single word wider than `max` stays on its own line). */
function wrap(text: string, font: FontKey, size: number, max: number): string[] {
  const out: string[] = []; let cur = '';
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const next = cur ? cur + ' ' + word : word;
    if (cur && textWidth(next, font, size) > max) { out.push(cur); cur = word; } else cur = next;
  }
  if (cur) out.push(cur);
  return out.length ? out : [''];
}

/** One drawing operation in item coordinates (points, origin top left of the item, before tilt). */
export type DrawOp =
  | { op: 'rect'; x: number; y: number; w: number; h: number; color: string; width: number; dash?: number[]; fill?: string }
  | { op: 'text'; x: number; y: number; size: number; font: FontKey; text: string; color: string }
  | { op: 'image'; key: string; x: number; y: number; w: number; h: number };
export interface Layout { ops: DrawOp[]; overflow: boolean }

const INK = '#231F20', MUTED = '#6F6A6C', PAD = 5, MARK = 9, MIN = 5.5;

/**
 * The item as drawing operations (same for LayerView and burnLayer). Font sizes start at the preferred size and shrink
 * (to 5.5 pt at the least) until everything fits w × h; `overflow` = it still does not fit (the program can warn).
 */
export function layoutItem(it: LayerItem): Layout {
  if (!isDrawn(it)) return { ops: [], overflow: false };
  const w = it.w, h = it.h, ops: DrawOp[] = [];
  const border = it.border ?? (it.kind === 'box' ? 'dashed' : it.kind === 'stamp' ? 'solid' : 'none');
  if (border !== 'none') ops.push({ op: 'rect', x: 0.6, y: 0.6, w: w - 1.2, h: h - 1.2, color: it.accent, width: it.kind === 'stamp' ? 1.4 : 0.9, ...(border === 'dashed' ? { dash: [3, 2] } : {}), fill: '#FFFFFF' });
  if (it.mark) ops.push({ op: 'image', key: it.mark, x: w - PAD - MARK, y: PAD, w: MARK, h: MARK });
  const inner = w - 2 * PAD, imgW = it.image ? Math.min(h - 2 * PAD, inner / 3) : 0;
  if (it.image) ops.push({ op: 'image', key: it.image, x: PAD, y: (h - imgW) / 2, w: imgW, h: imgW });
  const left = PAD + (imgW ? imgW + 4 : 0), avail = w - left - PAD;

  for (let k = 1; k >= MIN / 9; k -= 0.04) {
    const out: DrawOp[] = []; let y = PAD, ok = true;
    if (it.title) {
      const s = 7 * k; y += s * 0.8;
      out.push({ op: 'text', x: left, y, size: s, font: 'display', text: it.title.toUpperCase(), color: it.accent });
      y += s * 0.55;
    }
    for (const r of it.rows ?? []) {
      const s = 8 * k, lf: FontKey = r.strong ? 'bold' : 'body', vf: FontKey = r.strong ? 'monoBold' : 'mono';
      const lw = textWidth(r.label, lf, s), vw = textWidth(r.value, vf, s);
      if (lw + vw + 6 > avail) ok = false;
      y += s * 1.25;
      out.push({ op: 'text', x: left, y, size: s, font: lf, text: r.label, color: r.strong ? INK : MUTED });
      out.push({ op: 'text', x: left + avail - vw, y, size: s, font: vf, text: r.value, color: INK });
      if (r.strong) y += s * 0.15;
    }
    for (const l of it.lines ?? []) {
      const s = (l.size ?? (it.kind === 'stamp' ? 10 : 8)) * k, f: FontKey = l.mono ? (l.bold ? 'monoBold' : 'mono') : l.bold ? 'bold' : it.kind === 'stamp' ? 'display' : 'body';
      for (const t of wrap(l.text, f, s, avail)) {
        const tw = textWidth(t, f, s);
        if (tw > avail) ok = false;
        y += s * 1.2;
        const align = l.align ?? (it.kind === 'stamp' ? 'center' : 'left');
        const x = align === 'center' ? left + (avail - tw) / 2 : align === 'right' ? left + avail - tw : left;
        out.push({ op: 'text', x, y, size: s, font: f, text: t, color: l.color ?? (it.kind === 'stamp' ? it.accent : INK) });
      }
    }
    // A stamp's lines sit in the middle of its height.
    if (it.kind === 'stamp' && y + PAD < h) { const dy = (h - PAD - y) / 2; for (const o of out) if (o.op === 'text') o.y += dy; }
    if (ok && y + PAD <= h + 0.01) return { ops: ops.concat(out), overflow: false };
    if (k - 0.04 < MIN / 9) return { ops: ops.concat(out), overflow: true };
  }
  return { ops, overflow: true };
}

/** A point of the item (after layout) → page coordinates, with tilt around the item's centre. */
export function placeOnPage(it: LayerItem, x: number, y: number): { x: number; y: number } {
  const a = ((it.tilt ?? 0) * Math.PI) / 180, cx = it.w / 2, cy = it.h / 2, dx = x - cx, dy = y - cy;
  return { x: it.x + cx + dx * Math.cos(a) - dy * Math.sin(a), y: it.y + cy + dx * Math.sin(a) + dy * Math.cos(a) };
}
